import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { useI18n } from "@/i18n";
import { PageHeader, EmptyState } from "@/components/common";
import { ListFilter, useListFilter, csvMoney } from "@/components/common/ListFilter";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Boxes } from "lucide-react";
import BundleCard from "@/components/bundles/BundleCard";
import BundleFormDialog from "@/components/bundles/BundleFormDialog";

const EMPTY = { sku: "", name: "", price_net: "", price_gross: "", tax_rate: 19, strap_option: false, components: [] };

export default function Bundles() {
  const { user } = useAuth();
  const { t } = useI18n();
  const canEdit = can(user, "bundles:edit");
  const canCost = can(user, "finance:view");
  const [rows, setRows] = useState(null);
  const [variants, setVariants] = useState([]);
  const [availMap, setAvailMap] = useState({});
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [settings, setSettings] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(() => {
    api.get(`/bundles${showArchived ? "?include_archived=true" : ""}`).then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/variants").then((r) => setVariants([...r.data].sort((a, b) => (a.product_name || "").localeCompare(b.product_name || "", "de") || (a.sku || "").localeCompare(b.sku || "", "de")))).catch(() => {});
    api.get("/inventory").then((r) => setAvailMap(Object.fromEntries(r.data.map((x) => [x.variant_id, x.available])))).catch(() => {});
  }, [showArchived]);
  useEffect(() => { load(); api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, [load]);

  const taxRates = settings?.tax_rates || [0, 7, 19];
  const defaultTax = settings?.default_tax_rate ?? 19;
  const r2 = (n) => Math.round(n * 100) / 100;
  const setPrice = (key, value) => {
    const tax = (Number(f.tax_rate) || 0) / 100;
    const next = { ...f, [key]: value };
    if (key === "price_net") next.price_gross = value !== "" && !isNaN(Number(value)) ? String(r2(Number(value) * (1 + tax))) : "";
    else if (key === "price_gross") next.price_net = value !== "" && !isNaN(Number(value)) ? String(r2(Number(value) / (1 + tax))) : "";
    setF(next);
  };
  const setTax = (v) => { const tax = (Number(v) || 0) / 100; const n = Number(f.price_net); setF({ ...f, tax_rate: Number(v), price_gross: f.price_net !== "" && !isNaN(n) ? String(r2(n * (1 + tax))) : f.price_gross }); };

  const openNew = () => { setEditId(null); setF({ ...EMPTY, tax_rate: defaultTax }); setOpen(true); };
  const openEdit = (b) => {
    setEditId(b.id);
    setF({
      sku: b.sku, name: b.name, price_net: b.price_net ?? "", price_gross: b.price_gross ?? "",
      tax_rate: b.tax_rate ?? defaultTax, strap_option: !!b.strap_option,
      components: b.components.map((c) => ({
        _k: crypto.randomUUID(),
        component_variant_id: c.missing ? "" : c.component_variant_id,
        quantity: c.quantity, is_free_component: !!c.is_free_component,
      })),
    });
    setOpen(true);
  };

  const addComp = () => setF({ ...f, components: [...f.components, { _k: crypto.randomUUID(), component_variant_id: "", quantity: 1, is_free_component: false }] });
  const dimStr = (v) => { const u = v.dimension_uom || "cm"; if (v.diameter_cm) return `⌀ ${v.diameter_cm} ${u}`; const p = [v.length_cm, v.width_cm, v.height_cm].filter((x) => x != null && x !== ""); return p.length ? `${p.join("×")} ${u}` : ""; };
  const optionsFor = (i) => { const used = f.components.map((x, j) => (j !== i ? x.component_variant_id : null)).filter(Boolean); return variants.filter((v) => !used.includes(v.id)); };
  const updComp = (i, k, val) => { const c = [...f.components]; c[i] = { ...c[i], [k]: val }; setF({ ...f, components: c }); };
  const rmComp = (i) => setF({ ...f, components: f.components.filter((_, j) => j !== i) });

  const sellable = f.components.length ? Math.max(0, Math.min(...f.components.map((c) =>
    Math.floor((availMap[c.component_variant_id] ?? 0) / (Number(c.quantity) || 1))))) : 0;
  const compCostNet = f.components.reduce((s, c) => { const v = variants.find((x) => x.id === c.component_variant_id); return s + (Number(v?.cost_price) || 0) * (Number(c.quantity) || 1); }, 0);
  const netNum = Number(f.price_net) || 0;
  const grossNum = Number(f.price_gross) || 0;
  const ertragNum = r2(netNum - compCostNet);

  const save = async () => {
    const payload = {
      sku: f.sku, name: f.name, bundle_type: "fest", tax_rate: Number(f.tax_rate) || 0, strap_option: !!f.strap_option,
      price_net: Number(f.price_net) || 0, price_gross: Number(f.price_gross) || 0,
      components: f.components.map((c) => ({ ...c, quantity: Number(c.quantity) || 1 })),
    };
    try {
      if (editId) { await api.put(`/bundles/${editId}`, payload); toast.success(t("bundle.updated")); }
      else { await api.post("/bundles", payload); toast.success(t("bundle.created")); }
      setOpen(false); setF(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const duplicate = async (b) => {
    try { const r = await api.post(`/bundles/${b.id}/duplicate`); toast.success(t("bundle.duplicated", { sku: r.data.sku })); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const copyToMesse = async (b) => {
    if (!window.confirm(t("bundle.toMesseConfirm", { name: b.name }))) return;
    try { const r = await api.post(`/bundles/${b.id}/copy-to-messe`); toast.success(t("bundle.toMesseDone", { sku: r.data.sku })); }
    catch (e) { toast.error(apiError(e)); }
  };
  const archive = async (b) => {
    try { await api.post(`/bundles/${b.id}/archive`); toast.success(t("bundle.archived")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const unarchive = async (b) => {
    try { await api.post(`/bundles/${b.id}/unarchive`); toast.success(t("bundle.reactivated")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (b) => {
    if (!window.confirm(t("bundle.confirmDelete", { name: b.name }))) return;
    try { await api.delete(`/bundles/${b.id}`); toast.success(t("bundle.deleted")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const compText = (b) => (b.components || []).map((c) => `${c.variant_sku || ""} ${c.variant_name || ""}`).join(" ");
  const filter = useListFilter(rows, { fields: ["name", "sku"], searchOf: compText, storageKey: "bundles" });
  const view = filter.filtered;

  return (
    <div>
      <PageHeader title={t("bundle.title")} subtitle={t("bundle.subtitle")}
        actions={<div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer mr-1" data-testid="bundles-show-archived-label">
            <Checkbox checked={showArchived} onCheckedChange={(v) => setShowArchived(!!v)} data-testid="bundles-show-archived" /> {t("common.showArchived")}
          </label>
          {canEdit && <Button data-testid="new-bundle-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1.5" /> {t("bundle.new")}</Button>}
        </div>} />

      <ListFilter filter={filter} testId="bundles" placeholder={t("filter.searchProductSku")}
        exportName="bundles" exportColumns={[
          { label: "SKU", value: (b) => b.sku },
          { label: t("common.name"), value: (b) => b.name },
          { label: t("common.net"), value: (b) => csvMoney(b.price_net) },
          { label: t("common.gross"), value: (b) => csvMoney(b.price_gross) },
          { label: t("set.vatPercent"), value: (b) => b.tax_rate },
          { label: t("inv.available"), value: (b) => b.sellable ?? 0 },
          { label: t("bundle.components"), value: (b) => (b.components || []).map((c) => `${c.variant_sku || ""}${c.variant_name ? ` · ${c.variant_name}` : ""} x${c.quantity}`).join(" | ") },
          { label: t("common.archived"), value: (b) => (b.archived ? t("common.yes", "ja") : t("common.no", "nein")) },
        ]} />

      {rows === null ? <div className="text-muted-foreground">{t("common.loading")}</div> :
        rows.length === 0 ? <EmptyState testId="bundles-empty" icon={Boxes} title={t("bundle.emptyTitle")} hint={t("bundle.emptyHint")} /> : (
          <div className="space-y-8">
            {[
              { key: "available", label: t("bundle.available"), items: view.filter((b) => (b.sellable || 0) > 0) },
              { key: "unavailable", label: t("bundle.unavailable"), items: view.filter((b) => (b.sellable || 0) <= 0) },
            ].map((sec) => sec.items.length === 0 ? null : (
              <section key={sec.key} data-testid={`bundles-section-${sec.key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`h-2.5 w-2.5 rounded-full ${sec.key === "available" ? "bg-primary" : "bg-destructive"}`} />
                  <h2 className="font-heading text-lg font-medium">{sec.label}</h2>
                  <span className="text-sm text-muted-foreground">({sec.items.length})</span>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid={`bundles-list-${sec.key}`}>
                  {sec.items.map((b) => (
                    <BundleCard key={b.id} b={b} canEdit={canEdit} canCost={canCost} onEdit={openEdit} onDuplicate={duplicate} onArchive={archive} onUnarchive={unarchive} onDelete={del} onCopyToMesse={copyToMesse} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

      <BundleFormDialog
        open={open} onOpenChange={setOpen} editId={editId} f={f} setF={setF}
        setPrice={setPrice} setTax={setTax} taxRates={taxRates}
        addComp={addComp} updComp={updComp} rmComp={rmComp} optionsFor={optionsFor}
        dimStr={dimStr} availMap={availMap} save={save} load={load}
        sellable={sellable} compCostNet={compCostNet} netNum={netNum} grossNum={grossNum} ertragNum={ertragNum}
      />
    </div>
  );
}
